// noinspection JSUnusedLocalSymbols

import { DestroyRef, inject, Injectable } from '@angular/core';
import { BaseViewModel } from '../../../../../models/base/base-view-model';
import { UserDomainModel } from '../../../../../domainModels/user-domain-model';
import { SignInRequest } from '../../../../../models/users/requests/sign-in-request';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastService } from '../../../../../services/toast-service';
import { PortalType } from '../../../../../models/enum/shared/portal-type';
import { UserChallengeName } from '../../../../../models/users/enum/user-challenge.name';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject } from 'rxjs';
import { HttpStatusCode } from '@angular/common/http';
import { CustomError } from '../../../../../models/shared/custom-error';
import { Routes } from '../../../../../../routes/routes';

@Injectable()
export class SignInViewModel extends BaseViewModel {
  private readonly loadingMessage = $localize`Signing in`;
  private readonly invalidCredentialsMessage = $localize`The information you entered was incorrect.`;

  private destroyRef = inject(DestroyRef);
  private userDomainModel = inject(UserDomainModel);
  private router = inject(Router);
  private toastService = inject(ToastService);
  private route = inject(ActivatedRoute);

  private _signInFailed = new BehaviorSubject<boolean>(false);
  public readonly signInFailed$ = this._signInFailed.asObservable();

  public formGroup = new FormGroup({
    email: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required, Validators.email]
    }),
    password: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required]
    })
  });

  private listenToUrlParams = this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
    next: params => {
      const email = decodeURIComponent(decodeURIComponent(params?.Email || params?.email || ''));
      if (!!email) {
        this.formGroup.patchValue({ email });
        this.formGroup.updateValueAndValidity();
      }
    }
  });

  private clearFailedStateOnEdit = this.formGroup.controls.password.valueChanges
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(() => this._signInFailed.next(false));

  public internalUserSignIn(req: SignInRequest): void {
    this._loadingOpts.addRequest(this.loadingMessage);
    this.userDomainModel.signIn(req, PortalType.Internal).subscribe({
      next: user => {
        const { challengeName } = user;

        switch (challengeName) {
          case UserChallengeName.NEW_PASSWORD_REQUIRED:
            this.router.navigate(['/auth/internal-user-choose-password'], { replaceUrl: true }).then(() => {});
            break;
          case UserChallengeName.MFA_SETUP:
            this.router.navigate(['/auth/internal-user-auth-setup'], { replaceUrl: true }).then(() => {});
            break;
          case UserChallengeName.SOFTWARE_TOKEN_MFA:
            this.router.navigate(['/auth/internal-user-verify-code'], { replaceUrl: true }).then(() => {});
            break;
          default:
            this.router.navigate(['/auth/sign-in'], { replaceUrl: true }).then();
            break;
        }
        this._loadingOpts.removeRequest(this.loadingMessage);
      },
      error: error => {
        this.toastService.publishError(error);
        this._loadingOpts.removeRequest(this.loadingMessage);
      }
    });
  }

  public memberSignIn(req: SignInRequest): void {
    this._signInFailed.next(false);
    this._loadingOpts.addRequest(this.loadingMessage);
    this.userDomainModel.signIn(req, PortalType.Member).subscribe({
      next: user => {
        const { challengeName } = user;
        switch (challengeName) {
          case UserChallengeName.SOFTWARE_TOKEN_MFA:
            this.router.navigate(['/auth/verify-mfa'], { replaceUrl: true }).then(() => {});
            break;
          case UserChallengeName.SMS_MFA:
            this.router.navigate(['/auth/verify-mfa'], { replaceUrl: true }).then(() => {});
            break;
          default:
            this.router.navigate(['/dashboard'], { replaceUrl: true }).then(() => {});
            break;
        }
        this._loadingOpts.removeRequest(this.loadingMessage);
      },
      error: (error: CustomError) => {
        if (error?.code === HttpStatusCode.Locked) {
          this.router.navigate([Routes.accessRestricted()], { replaceUrl: true }).then(() => {});
        } else {
          this._signInFailed.next(true);
          this.toastService.publishErrorMessage(this.invalidCredentialsMessage);
        }
        this._loadingOpts.removeRequest(this.loadingMessage);
      }
    });
  }

  public employerSignIn(req: SignInRequest): void {
    this._signInFailed.next(false);
    this._loadingOpts.addRequest(this.loadingMessage);
    this.userDomainModel.signIn(req, PortalType.Employer).subscribe({
      next: user => {
        const { challengeName } = user;
        switch (challengeName) {
          case UserChallengeName.NEW_PASSWORD_REQUIRED:
            this.router.navigate(['/auth/employer-choose-password'], { replaceUrl: true }).then(() => {});
            break;
          case UserChallengeName.SOFTWARE_TOKEN_MFA:
            this.router.navigate(['/auth/verify-mfa'], { replaceUrl: true }).then(() => {});
            break;
          case UserChallengeName.SMS_MFA:
            this.router.navigate(['/auth/verify-mfa'], { replaceUrl: true }).then(() => {});
            break;
          default:
            this.router.navigate(['/dashboard'], { replaceUrl: true }).then(() => {});
            break;
        }
        this._loadingOpts.removeRequest(this.loadingMessage);
      },
      error: error => {
        this._signInFailed.next(true);
        this.toastService.publishError(error);
        this._loadingOpts.removeRequest(this.loadingMessage);
      }
    });
  }
}
